<template lang="pug">
.bulk-customisations.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Home – all courses
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/bulk-audio"
        )
          | Bulk Audio
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/bulk-translation-jobs"
        )
          | Bulk Translation Jobs
    .ms-auto
    ToggleDark

  .flex-1.p-24.overflow-y-auto.overflow-x-visible
    .max-w-screen-2xl.mx-auto
      .prose.dark_prose-invert.max-w-none
        .flex.items-center.gap-4.mb-2
          h1.font-normal.m-0 Bulk Customisations
          a.text-sm.text-blue-600.dark_text-blue-400.hover_underline(href="#/bulk-translation-jobs")
            | View translation jobs →

        p
          span This tool will create customisations with translations for multiple courses.
            
        p 
          span It will:
          ul
            li Run base course translations
            li Create new/update existing customisations (runs translations if they contain content)
            li Publish each to production

        .p-4.my-6.bg-gray-600.dark_bg-gray-900.bg-opacity-10.rounded-lg
          .flex.flex-col.gap-4
            h2.p-0.mt-0 Course Selection
            .flex.flex-col.gap-2
              label.text-sm.font-medium.text-gray-700.dark_text-gray-300
                | Course IDs (comma-separated):
              input.w-full.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.text-gray-900.dark_text-gray-100(
                v-model="courseIdsInput"
                placeholder="e.g. 170, 895, 5009"
                :disabled="checkingCustomisations"
              )
              .text-xs.mt-1(v-if="courseIdsInput && courseIdsInput.trim()")
                .text-red-600.dark_text-red-400(v-if="courseIdValidationErrors && courseIdValidationErrors.length > 0")
                  .div(v-for="error in courseIdValidationErrors" :key="error") {{ error }}
                .text-green-600.dark_text-green-400(v-else-if="parseCourseIds(courseIdsInput) && parseCourseIds(courseIdsInput).length > 0")
                  | ✓ {{ parseCourseIds(courseIdsInput).length }} valid course ID(s)
            
            .flex.flex-col.gap-2
              label.text-sm.font-medium.text-gray-700.dark_text-gray-300
                | Firm Code:
              input.w-full.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.text-gray-900.dark_text-gray-100(
                v-model="firmCode"
                placeholder="e.g. customfirm123"
                :disabled="checkingCustomisations"
              )
              .text-xs.mt-1(v-if="firmCode && firmCode.trim()")
                .text-red-600.dark_text-red-400(v-if="firmCodeValidationErrors && firmCodeValidationErrors.length > 0")
                  .div(v-for="error in firmCodeValidationErrors" :key="error") {{ error }}
                .text-green-600.dark_text-green-400(v-else-if="isValidFirmCode")
                  | ✓ Valid firm code
            
            .flex.flex-col.gap-2
              label.text-sm.font-medium.text-gray-700.dark_text-gray-300
                | Languages (comma-separated):
              input.w-full.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.text-gray-900.dark_text-gray-100(
                v-model="languageCodes"
                placeholder="e.g. el, fr, de"
                :disabled="checkingCustomisations"
              )
              .text-xs.mt-1(v-if="languageCodes && languageCodes.trim()")
                .text-red-600.dark_text-red-400(v-if="languageValidationErrors && languageValidationErrors.length > 0")
                  .div(v-for="error in languageValidationErrors" :key="error") {{ error }}
                .text-green-600.dark_text-green-400(v-else-if="parseLanguageCodes(languageCodes) && parseLanguageCodes(languageCodes).length > 0")
                  | ✓ {{ parseLanguageCodes(languageCodes).length }} valid language code(s)

            .flex.flex-col.gap-2
              label.text-sm.font-medium.text-gray-700.dark_text-gray-300
                span Translation Provider:
              select.w-full.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.text-gray-900.dark_text-gray-100(v-model="translationProvider")
                option.text-gray-900.dark_text-gray-100.bg-white.dark_bg-gray-900(value="azure") Azure
                option.text-gray-900.dark_text-gray-100.bg-white.dark_bg-gray-900(value="openai") OpenAI

            .flex.gap-2.mt-6
              button.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                type="button"
                @click="checkCustomisations"
                :disabled="!isValidInput || checkingCustomisations || isProcessing"
              )
                span(v-if="!checkingCustomisations") Validate Courses
                span(v-else) Validating...
              
              button.btn-hover_cursor-pointer.bg-red-600.btn-hover_bg-gray-600.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                type="button"
                @click="clearAll"
                :disabled="checkingCustomisations || isProcessing"
              )
                span Clear All

              button.btn-hover_cursor-pointer.bg-blue-600.btn-hover_bg-blue-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                type="button"
                @click="copyUrlToClipboard"
                title="Create and copy a URL with the current input values"
                :disabled="!isValidInput || checkingCustomisations || isProcessing"
              )
                span Copy as URL

        // Processing Table
        .p-4.my-6.bg-gray-600.dark_bg-gray-900.bg-opacity-10.rounded-lg(v-if="coursesToProcess.length > 0")
          .flex.justify-between.items-center.mb-4
            h2.p-0.mt-0 Processing Status
            .flex.gap-2
              button.btn-hover_cursor-pointer.bg-orange-600.btn-hover_bg-orange-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                type="button"
                @click="retryFailedCourses"
                :disabled="failedCourses.length === 0"
                v-if="failedCourses.length > 0"
              )
                span Retry Failed ({{ failedCourses.length }})
              button.btn-hover_cursor-pointer.bg-gray-600.btn-hover_bg-gray-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                type="button"
                @click="resetTableAndStatuses"
              )
                span Clear
          
          p
            | Found {{ coursesToProcess.length }} base course(s) with {{ existingCustomisationsCount }} existing customisation(s). {{ coursesToProcess.length - existingCustomisationsCount }} new customisation(s) will be created.
          
          p.text-red-600.dark_text-red-400.font-bold(v-if="invalidCoursesCount > 0")
            | ❌ {{ invalidCoursesCount }} course(s) failed validation!
          
          // Select All Checkbox
          .flex.items-center.gap-2.mb-4
            input#select-all(
              type="checkbox"
              :checked="allCoursesSelected"
              @change="toggleSelectAll"
              :disabled="isProcessing"
            )
            label.cursor-pointer.text-sm.font-medium(
              for="select-all"
            )
              | Select All ({{ selectedCourses.size }}/{{ coursesToProcess.length }})
          
          .overflow-x-auto.overflow-y-auto.max-h-96
            table.w-full.text-sm.m-0.bg-white.dark_bg-gray-800
              thead
                tr.border-b.border-gray-300.dark_border-gray-600
                  th.text-center.align-middle.py-2.px-2 Include
                  th.text-center.align-middle.py-2.px-2 Base Status
                  th.text-center.align-middle.py-2.px-2 Custom Status
                  th.text-center.align-middle.py-2.px-2 Course ID
                  th.text-center.align-middle.py-2.px-2 Firm Code
                  th.text-center.align-middle.py-2.px-2 Languages
                  th.text-center.align-middle.py-2.px-2 Base Translations
                  th.text-center.align-middle.py-2.px-2 Custom Translations
                  th.text-center.align-middle.py-2.px-2 Base Publish
                  th.text-center.align-middle.py-2.px-2 Custom Publish
              tbody
                tr.border-b.border-gray-200.dark_border-gray-700(
                  v-for="course in coursesToProcess"
                  :key="`${course.firmCode}_${course.courseId}`"
                )
                  td.py-2.px-2.text-center
                    input(
                      type="checkbox"
                      :checked="isCourseSelected(course)"
                      @change="toggleCourseSelection(course)"
                      :disabled="isProcessing || !isCourseValidForSelection(course)"
                    )
                  td.py-2.px-2.text-center
                    .flex.items-center.justify-center.gap-1
                      span.text-sm(v-if="validatingCourses")
                        span.text-gray-500 Validating...
                      span.text-sm(v-else-if="getBaseCourseValidationStatus(course) === 'valid'")
                        span.text-green-600.dark_text-green-400.font-bold(:title="getBaseCourseValidationTooltip(course)") ✓
                      span.text-sm(v-else-if="getBaseCourseValidationStatus(course) === 'invalid'")
                        span.text-red-600.dark_text-red-400.font-bold(:title="getBaseCourseValidationTooltip(course)") {{ getValidationStatus(course)?.baseCourseErrorCode === 404 ? '❌' : '⚠️' }}
                        a.text-blue-600.dark_text-blue-400.hover_text-blue-800.dark_hover_text-blue-300(
                        :href="`#/editor/${course.courseId}`"
                        title="Edit course"
                        target="_blank"
                        v-if="getValidationStatus(course)?.baseCourseErrorCode !== 404"
                        )
                          span.text-sm 📝
                        //- span.text-xs {{ getValidationStatus(course)?.baseCourseErrorCode }}
                      span.text-sm(v-else)
                        span.text-gray-500(:title="getBaseCourseValidationTooltip(course)") ⏳
                      
                  td.py-2.px-2.text-center
                    .flex.items-center.justify-center.gap-1
                      span.text-sm(v-if="validatingCourses")
                        span.text-gray-500 Validating...
                      
                      span.text-sm(v-else-if="getCustomisationValidationStatus(course) === 'valid'")
                        span.text-xs.text-blue-600.dark_text-blue-400.font-medium.ms-1(v-if="!hasExistingCustomisation(course)") NEW
                        span.text-green-600.dark_text-green-400.font-bold(:title="getCustomisationValidationTooltip(course)" v-else) ✓
                        
                      span.text-sm(v-else-if="getCustomisationValidationStatus(course) === 'invalid'")
                        span.text-red-600.dark_text-red-400.font-bold(:title="getCustomisationValidationTooltip(course)") {{ getValidationStatus(course)?.customisationErrorCode === 404 ? '❌' : '⚠️' }}
                      
                      span.text-sm(v-else)
                        span.text-gray-500(:title="getCustomisationValidationTooltip(course)") ⏳
                      
                      a.text-blue-600.dark_text-blue-400.hover_text-blue-800.dark_hover_text-blue-300(
                        :href="`#/editor/${course.firmCode ? course.firmCode + '_' : ''}${course.courseId}`"
                        title="Edit customisation"
                        target="_blank"
                        v-if="getValidationStatus(course)?.baseCourseValid && getValidationStatus(course)?.customisationErrorCode !== 404 && hasExistingCustomisation(course)"
                      )
                        span.text-sm 📝
                      
                  td.py-2.px-2.font-mono.text-center {{ course.courseId }}
                  td.py-2.px-2.font-mono.text-center.break-words(class="max-w-[8rem]" :title="course.firmCode") {{ course.firmCode }}
                  td.py-2.px-2.text-center
                    span.text-sm(v-if="getTranslationLanguages(course).length > 0") {{ getTranslationLanguages(course).join(', ') }}
                    span.text-gray-500.text-sm(v-else) None
                  td.py-2.px-2.text-center
                    span.text-sm(v-if="getBaseTranslationStatus(course) === 'pending'") 
                      span.text-yellow-600.dark_text-yellow-400 
                        | {{ getBaseLanguageProgress(course) || 'Pending' }}
                    span.text-sm(v-else-if="getBaseTranslationStatus(course) === 'success'") 
                      span.text-green-600.dark_text-green-400 ✓ Success
                    span.text-sm(v-else-if="getBaseTranslationStatus(course) === 'error'") 
                      span.text-red-600.dark_text-red-400 ❌ Failed
                    span.text-sm(v-else) 
                      span.text-gray-500 Not Started
                  td.py-2.px-2.text-center
                    span.text-sm(v-if="getCustomTranslationStatus(course) === 'pending'") 
                      span.text-yellow-600.dark_text-yellow-400 
                        | {{ getCustomLanguageProgress(course) || 'Pending' }}
                    span.text-sm(v-else-if="getCustomTranslationStatus(course) === 'success'") 
                      span.text-green-600.dark_text-green-400 ✓ Success
                    span.text-sm(v-else-if="getCustomTranslationStatus(course) === 'error'") 
                      span.text-red-600.dark_text-red-400 ❌ Failed
                    span.text-sm(v-else-if="getCustomTranslationStatus(course) === 'skipped'") 
                      span.text-blue-600.dark_text-blue-400 ⏭️ Skipped
                    span.text-sm(v-else) 
                      span.text-gray-500 Not Started
                  td.py-2.px-2.text-center
                    span.text-sm(v-if="getBasePublishingStatus(course) === 'pending'") 
                      span.text-yellow-600.dark_text-yellow-400 Pending
                    span.text-sm(v-else-if="getBasePublishingStatus(course) === 'success'") 
                      span.text-green-600.dark_text-green-400 ✓ Success
                    span.text-sm(v-else-if="getBasePublishingStatus(course) === 'error'") 
                      span.text-red-600.dark_text-red-400 ❌ Failed
                    span.text-sm(v-else) 
                      span.text-gray-500 Not Started
                  td.py-2.px-2.text-center
                    span.text-sm(v-if="getCustomPublishingStatus(course) === 'pending'") 
                      span.text-yellow-600.dark_text-yellow-400 Pending
                    span.text-sm(v-else-if="getCustomPublishingStatus(course) === 'success'") 
                      span.text-green-600.dark_text-green-400 ✓ Success
                    span.text-sm(v-else-if="getCustomPublishingStatus(course) === 'error'") 
                      span.text-red-600.dark_text-red-400 ❌ Failed
                    span.text-sm(v-else) 
                      span.text-gray-500 Not Started

        // Processing Controls
        .p-4.my-6.bg-gray-600.dark_bg-gray-900.bg-opacity-10.rounded-lg(v-if="coursesToProcess.length > 0")
          .flex.flex-col.gap-2.mb-4
            h2.p-0.mt-0 Processing Controls

          //- once we have successfully processed some/all of the courses, we can go to Bulk Audio
          .flex.gap-2.mb-4(v-if="processingResults.filter(result => result.success).length > 0")
            button.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
              type="button"
              @click="gotoBulkAudio"
            )
              span Go to Bulk Audio

            button.btn-hover_cursor-pointer.bg-blue-600.btn-hover_bg-blue-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
              type="button"
              @click="gotoHome"
            )
              span Go Home
          
          .flex.gap-2.mb-2(v-else-if="processingResults.filter(result => result.success).length === 0")
            button.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
              type="button"
              @click="startBulkCustomisations"
              :disabled="isProcessing || selectedCourses.size === 0"
            )
              span(v-if="!isProcessing") Start Bulk Customisations ({{ selectedCourses.size }})
              span(v-else) Processing...

            button.btn-hover_cursor-pointer.bg-red-600.btn-hover_bg-red-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
              type="button"
              @click="stopProcessing"
              :disabled="!isProcessing"
            )
              span Stop Processing

          .flex.items-center.gap-3.mb-4(v-if="processingResults.filter(result => result.success).length === 0")
            button.btn-hover_cursor-pointer.bg-purple-600.btn-hover_bg-purple-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
              type="button"
              @click="queueForBackgroundProcessing"
              :disabled="isProcessing || isQueuingJob || selectedCourses.size === 0"
            )
              span(v-if="!isQueuingJob") Send to background process ({{ selectedCourses.size }})
              span(v-else) Queueing...

            label.flex.items-center.gap-2.text-sm.text-gray-700.dark_text-gray-300.cursor-pointer.select-none
              input(
                type="checkbox"
                v-model="bulkJobRefreshAi"
              )
              span Refresh existing AI translations
            label.flex.items-center.gap-2.text-sm.text-gray-700.dark_text-gray-300.cursor-pointer.select-none
              input(
                type="checkbox"
                v-model="bulkJobPublishToProduction"
              )
              span Publish to production

          p.text-sm.mt-1(v-if="queueJobMessage" v-html="queueJobMessage")
</template>

<script setup lang="ts">
import MainMenu from './MainMenu.vue';
import ToggleDark from './ToggleDark.vue';
import useDocTitle from '../lib/useDocTitle';
import getJWT from '../../lib/getJWT';
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  getLatestDraft,
  getLatestSaved,
  getLatestDraftOrSaved,
  createAiTranslations,
  createNewDraft,
  updateDraft,
  commitDraft,
  publishCourse,
  countCustomisations
} from '../../lib/callModContent';
import {
  publishTranslations,
  submitBulkTranslationJob
} from '../lib/callModContentAuthors';
import {
  parseHashParams,
  generateHashUrl,
  updateHashUrl,
  getHashParam,
  setHashParam
} from '../lib/hashParams';
import languagesMap from '../../lib/languages.json';
import activeLanguages from '../../lib/activeLanguages.yml';

declare module '../../lib/activeLanguages.yml' {
  const arr: string[];
  // @ts-ignore
  export default arr;
}

const docTitle = computed(() => 'Bulk Customisations');
const route = useRoute();

useDocTitle(docTitle);

// Language validation setup (similar to AuthorTranslate.vue)
const validLanguages = Object.keys(languagesMap)
  .filter(code => activeLanguages.includes(code))
  .filter(code => !languagesMap[code].deprecated)
  .map(code => ({
    code,
    ...languagesMap[code]
  }));

const deprecatedLanguages = Object.keys(languagesMap)
  .filter(code => languagesMap[code].deprecated)
  .map(code => ({
    code,
    ...languagesMap[code]
  }));

// Reactive data
const courseIdsInput = ref<string>('');
const firmCode = ref<string>('');
const languageCodes = ref<string>('');
const translationProvider = ref<string>('openai');
const checkingCustomisations = ref<boolean>(false);

// Processing state
const isProcessing = ref<boolean>(false);
const shouldStopProcessing = ref<boolean>(false);
const isQueuingJob = ref<boolean>(false);
const queueJobMessage = ref<string>('');
const bulkJobRefreshAi = ref<boolean>(true);
const bulkJobPublishToProduction = ref<boolean>(true);

// Draft creation state
const isCreatingDrafts = ref<boolean>(false);
const currentDraftProcessing = ref('');
const completedDrafts = ref<number>(0);
const totalDrafts = ref<number>(0);

// Translation state
const isCreatingTranslations = ref<boolean>(false);
const currentTranslationProcessing = ref('');
const completedTranslations = ref<number>(0);
const totalTranslations = ref<number>(0);

// Publishing state
const isPublishing = ref<boolean>(false);
const currentPublishingProcessing = ref('');
const completedPublishing = ref<number>(0);
const totalPublishing = ref<number>(0);

// Course selection
const selectedCourses = ref<Set<string>>(new Set());

// Results tracking
const processingResults = ref<ProcessingResult[]>([]);

// Validation tracking
const courseValidationStatus = ref<
  Map<
    string,
    {
      baseCourseValid: boolean;
      customisationValid: boolean;
      hasExistingDraftOrSaved: boolean;
      translationsReady: boolean;
      baseCourseError?: string;
      customisationError?: string;
      baseCourseErrorCode?: number;
      customisationErrorCode?: number;
    }
  >
>(new Map());
const validatingCourses = ref(false);

// Store existing customisations from validation phase
const existingCustomisations = ref<Map<number, any>>(new Map());

// Status tracking for each course
const courseStatus = ref<Map<string, CourseStatus>>(new Map());

// Generate URL with current parameters using hash-based approach
const generateUrl = (type: 'customisations' | 'audio') => {
  const params = new URLSearchParams();

  if (courseIdsInput.value.trim()) {
    const selectedCourseIds = Array.from(selectedCourses.value).map(
      courseKey => courseKey.split('_')[1]
    );

    if (selectedCourseIds.length === 0) return '';

    params.set('courseIds', selectedCourseIds.join(','));
  }

  if (firmCode.value.trim()) {
    params.set('firmCode', firmCode.value.trim());
  }

  if (languageCodes.value.trim()) {
    params.set('languages', languageCodes.value.trim());
  }

  if (translationProvider.value.trim()) {
    params.set('translationProvider', translationProvider.value.trim());
  }

  if (type === 'customisations') {
    params.set('autoValidate', 'true');
    return generateHashUrl('/bulk-customisations', params);
  } else if (type === 'audio') {
    params.set('autoLoad', 'true');
    return generateHashUrl('/bulk-audio', params);
  } else if (type === 'home') {
    return generateHashUrl('/home', new URLSearchParams());
  }

  return '';
};

// Copy URL to clipboard
const copyUrlToClipboard = async () => {
  try {
    const url = generateUrl('customisations');
    if (url !== '') {
      await navigator.clipboard.writeText(url);
      log('✅ URL copied to clipboard');
    }
  } catch (error) {
    log('❌ Failed to copy URL to clipboard');
  }
};

const gotoBulkAudio = () => {
  const url = generateUrl('audio');
  if (url !== '') window.location.href = url;
};

const gotoHome = () => {
  const url = generateUrl('home');
  if (url !== '') window.location.href = url;
};

interface CourseStatus {
  baseTranslationStatus: 'idle' | 'pending' | 'success' | 'error';
  customTranslationStatus: 'idle' | 'pending' | 'success' | 'error' | 'skipped';
  basePublishingStatus: 'idle' | 'pending' | 'success' | 'error';
  customPublishingStatus: 'idle' | 'pending' | 'success' | 'error';
  baseTranslationError?: string;
  customTranslationError?: string;
  basePublishingError?: string;
  customPublishingError?: string;
  baseTranslationProgress?: string;
  customTranslationProgress?: string;
  basePublishingProgress?: string;
  customPublishingProgress?: string;
  currentBaseLanguage?: string;
  currentCustomLanguage?: string;
  baseLanguageProgress?: string;
  customLanguageProgress?: string;
}

interface CustomReport {
  courseId: number;
  total: number;
  translations: number;
  patches: number;
  audio: number;
  patchedAudio: number;
  patchedTranslations: number;
  list: any[];
}

interface ProcessingResult {
  courseKey: string;
  success: boolean;
  error?: string;
  details?: string;
}

// Logging function to replace console.log
const log = (message: string) => {
  // const timestamp = new Date().toISOString();
  // const log = `[${timestamp}] ${message}\n`;

  console.log(message);
};

// Helper function to extract HTTP error code from error message
const extractErrorCode = (error: any): number | undefined => {
  if (error?.response?.status) {
    return error.response.status;
  }

  // Check if error message contains HTTP status code in format [404] or [409]
  const match = error?.message?.match(/\[(\d+)\]/);
  if (match) {
    return parseInt(match[1]);
  }

  return undefined;
};

// Helper function to get user-friendly error description based on HTTP error code
const getErrorDescription = (errorCode: number): string => {
  switch (errorCode) {
    case 404:
      return 'Course not found - base course does not exist';
    case 409:
      return 'Translation dry run failed - course may have validation issues';
    case 400:
      return 'Bad request - invalid course parameters';
    case 401:
      return 'Unauthorized - authentication required';
    case 403:
      return 'Forbidden - insufficient permissions';
    case 500:
      return 'Internal server error - try again later';
    default:
      return `HTTP ${errorCode} error`;
  }
};

// API Functions using built-in methods
async function loadExistingDraft(courseId: number, firmCode: string) {
  try {
    return await getLatestDraft({ courseId, firmCode });
  } catch (error) {
    log(
      `Warning: Could not load draft (${courseId}, ${firmCode}): ${error.message}`
    );
    return null;
  }
}

async function loadExistingSaved(courseId: number, firmCode: string) {
  try {
    return await getLatestSaved({ courseId, firmCode });
  } catch (error) {
    log(
      `Warning: Could not load saved customisation (${courseId}, ${firmCode}): ${error.message}`
    );
    return null;
  }
}

async function loadExistingCustomisation(courseId: number, firmCode: string) {
  try {
    const [latestDraft, latestSaved] = await Promise.all([
      getLatestDraft({ courseId, firmCode }),
      getLatestSaved({ courseId, firmCode })
    ]);

    // Check for unsaved changes using the same logic as BulkAudio
    const hasUnsavedChanges =
      latestDraft &&
      (latestSaved
        ? latestDraft?.metadata.updatedDate > latestSaved?.metadata.createdDate
        : true);

    if (hasUnsavedChanges) {
      throw new Error(`Draft has unsaved changes (${courseId}, ${firmCode})`);
    }

    // Return the most recent (draft or saved)
    if (latestDraft && latestSaved) {
      // If both exist, use the newer one
      const isDraftNewer =
        latestDraft?.metadata.updatedDate > latestSaved?.metadata.createdDate;
      const type = isDraftNewer ? 'draft' : 'saved';
      const data = isDraftNewer ? latestDraft : latestSaved;

      log(`📄 Found existing ${type} for courseId=${courseId}`);
      return { type, data };
    } else if (latestDraft) {
      log(`📄 Found existing draft for courseId=${courseId}`);
      return { type: 'draft', data: latestDraft };
    } else if (latestSaved) {
      log(`💾 Found existing saved customisation for courseId=${courseId}`);
      return { type: 'saved', data: latestSaved };
    }

    return null;
  } catch (error) {
    throw new Error(
      `Failed to load existing customisation (${courseId}): ${error.message}`
    );
  }
}

async function checkTranslationsDryRun(courseId: number) {
  try {
    return await createAiTranslations({ courseId, to: 'en', dryRun: true });
  } catch (error) {
    throw new Error(
      `Translations dry run failed (${courseId}): ${error.message}`
    );
  }
}

// Helper functions for YAML processing
function extractLanguagesFromYaml(yaml: string): string[] {
  try {
    const lines = yaml.split('\n');
    const languages: string[] = [];
    let inLanguagesSection = false;

    for (const line of lines) {
      if (line.trim() === 'languages:') {
        inLanguagesSection = true;
        continue;
      }

      if (inLanguagesSection) {
        if (line.trim().startsWith('- ')) {
          const lang = line.trim().substring(2).trim();
          if (lang) {
            languages.push(lang);
          }
        } else if (line.trim() && !line.startsWith(' ')) {
          break;
        }
      }
    }

    return languages;
  } catch (error) {
    log(`Warning: Could not parse YAML languages: ${error.message}`);
    return [];
  }
}

function hasCustomisationContent(yaml: string): boolean {
  try {
    const lines = yaml.split('\n');
    let hasContent = false;
    let inLanguagesSection = false;

    for (const line of lines) {
      const trimmedLine = line.trim();

      if (!trimmedLine) continue;

      if (trimmedLine === 'languages:') {
        inLanguagesSection = true;
        continue;
      }

      if (inLanguagesSection) {
        if (trimmedLine.startsWith('- ')) {
          continue;
        } else if (trimmedLine && !trimmedLine.startsWith(' ')) {
          inLanguagesSection = false;
        } else {
          continue;
        }
      }

      if (!inLanguagesSection && trimmedLine) {
        hasContent = true;
        break;
      }
    }

    return hasContent;
  } catch (error) {
    log(`Warning: Could not check customisation content: ${error.message}`);
    return false;
  }
}

function updateLanguagesInYaml(yaml: string, newLanguages: string[]): string {
  try {
    const lines = yaml.split('\n');
    const result: string[] = [];
    let inLanguagesSection = false;
    let languagesSectionStart = -1;
    let languagesSectionEnd = -1;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.trim() === 'languages:') {
        inLanguagesSection = true;
        languagesSectionStart = i;
        continue;
      }

      if (inLanguagesSection) {
        if (line.trim().startsWith('- ')) {
          continue;
        } else if (line.trim() && !line.startsWith(' ')) {
          languagesSectionEnd = i;
          break;
        } else if (line.trim() === '') {
          continue;
        }
      }
    }

    if (languagesSectionStart !== -1) {
      for (let i = 0; i < languagesSectionStart; i++) {
        result.push(lines[i]);
      }

      result.push('languages:');
      for (const lang of newLanguages) {
        result.push(`  - ${lang}`);
      }

      const endIndex =
        languagesSectionEnd !== -1 ? languagesSectionEnd : lines.length;
      for (let i = endIndex; i < lines.length; i++) {
        result.push(lines[i]);
      }
    } else {
      result.push(...lines);
      if (yaml.trim() && !yaml.endsWith('\n')) {
        result.push('');
      }
      result.push('languages:');
      for (const lang of newLanguages) {
        result.push(`  - ${lang}`);
      }
    }

    return result.join('\n');
  } catch (error) {
    log(`Warning: Could not update YAML languages: ${error.message}`);
    const yamlLanguages = newLanguages.map(lang => `  - ${lang}`).join('\n');
    return `${yaml.trim()}\n\nlanguages:\n${yamlLanguages}`;
  }
}

// Draft and customisation functions
async function createDraft(
  courseId: number,
  firmCode: string,
  languageCodes: string[]
) {
  const yamlLanguages = languageCodes.map(lang => `  - ${lang}`).join('\n');
  const yaml = `languages:\n${yamlLanguages}`;

  const draft = {
    courseId,
    type: 'custom',
    activityName: 'Full Course',
    yaml: yaml,
    firmCode,
    ubercourseVersion: 'latest'
  };

  try {
    const result = await createNewDraft(draft);
    log(`✅ Draft created: ${firmCode} ${courseId}`);
    return result.data;
  } catch (error) {
    throw new Error(
      `Create draft failed (${courseId}, ${firmCode}): ${error.message}`
    );
  }
}

async function updateExistingCustomisation(
  courseId: number,
  firmCode: string,
  existingCustomisation: any,
  newLanguageCodes: string[]
) {
  try {
    const currentYaml = existingCustomisation.data.yaml || '';
    const currentLanguages = extractLanguagesFromYaml(currentYaml);
    const mergedLanguages = [
      ...new Set([...currentLanguages, ...newLanguageCodes])
    ];

    log(`🔄 Updating customisation for courseId=${courseId}`);
    log(`📝 Current languages: [${currentLanguages.join(', ')}]`);
    log(`➕ New languages: [${newLanguageCodes.join(', ')}]`);
    log(`🎯 Merged languages: [${mergedLanguages.join(', ')}]`);

    const updatedYaml = updateLanguagesInYaml(currentYaml, mergedLanguages);

    const updatedDraft = {
      ...existingCustomisation.data,
      yaml: updatedYaml
    };

    const result = await updateDraft(updatedDraft);
    log(`✅ Customisation updated for courseId=${courseId}`);
    return result.data;
  } catch (error) {
    throw new Error(
      `Failed to update customisation (${courseId}): ${error.message}`
    );
  }
}

async function saveDraft(courseId: number, firmCode: string) {
  try {
    // We need to get the current draft to save it
    const draft = await getLatestDraft({ courseId, firmCode });
    if (!draft) {
      throw new Error(`No draft found to save (${courseId}, ${firmCode})`);
    }

    const result = await commitDraft({
      draft,
      commitMessage: 'Saved by bulk customisation script'
    });

    return result.data;
  } catch (error) {
    throw new Error(
      `Save draft failed (${courseId}, ${firmCode}): ${error.message}`
    );
  }
}

// Translation and publishing functions
async function runTranslations(
  courseId: number,
  languageCode: string,
  firmCode: string | null = null
) {
  try {
    const params: any = {
      courseId,
      to: languageCode,
      refreshAi: true,
      provider: translationProvider.value
    };

    if (firmCode) {
      params.firmCode = firmCode;
    }

    return await createAiTranslations(params);
  } catch (error) {
    throw new Error(
      `Run translations failed (${courseId}, ${languageCode}): ${error.message}`
    );
  }
}

async function publishTranslationsForCourse(
  courseId: number,
  languageCode: string
) {
  try {
    return await publishTranslations({
      courseId,
      to: languageCode,
      publishTo: 'live',
      provider: translationProvider.value
    });
  } catch (error) {
    throw new Error(
      `Publish translations failed (${courseId}, ${languageCode}): ${error.message}`
    );
  }
}

async function publishBaseCourse(courseId: number) {
  try {
    // We need to get the latest saved course to publish it
    const course = await getLatestSaved({ courseId });
    if (!course) {
      throw new Error(`No saved course found to publish (${courseId})`);
    }

    const result = await publishCourse({
      draft: course,
      publishTo: 'production',
      publishData: {
        internal: 'Course published by bulk customisation script'
      }
    });

    return result.data;
  } catch (error) {
    throw new Error(
      `Publish base course failed (${courseId}): ${error.message}`
    );
  }
}

async function publishCustomCourse(courseId: number, firmCode: string) {
  try {
    // We need to get the latest saved customisation to publish it
    const customisation = await getLatestSaved({ courseId, firmCode });
    if (!customisation) {
      throw new Error(
        `No saved customisation found to publish (${courseId}, ${firmCode})`
      );
    }

    const result = await publishCourse({
      draft: customisation,
      publishTo: 'production',
      publishData: {
        internal: 'Course published by bulk customisation script'
      }
    });

    return result.data;
  } catch (error) {
    throw new Error(
      `Publish custom course failed (${courseId}, ${firmCode}): ${error.message}`
    );
  }
}

// Workflow functions
async function processLanguageForCourse(
  courseId: number,
  firmCode: string,
  languageCode: string,
  isExistingCustomisation: boolean = false,
  customisationYaml: string | null = null,
  languageIndex: number = 0,
  totalLanguages: number = 1,
  customisationCourseId?: number,
  customisationFirmCode?: string
) {
  const courseKey = `${firmCode}_${courseId}`;

  try {
    log(`🌐 Processing language ${languageCode} for courseId=${courseId}...`);

    // Update base translation progress
    updateCourseStatus(courseKey, 'currentBaseLanguage', languageCode);
    updateCourseStatus(
      courseKey,
      'baseLanguageProgress',
      `${languageIndex + 1}/${totalLanguages} (${languageCode})`
    );

    // Run translations for base course (always needed)
    log(`Running translations for ${languageCode} (base course content)...`);
    const baseTranslated = await runTranslations(courseId, languageCode, null);
    log(`✅ Base course translations done for ${languageCode}`);

    // Additionally translate customisation content if updating existing customisation with meaningful content
    if (
      isExistingCustomisation &&
      customisationYaml &&
      hasCustomisationContent(customisationYaml) &&
      customisationCourseId &&
      customisationFirmCode
    ) {
      // Update custom translation progress
      updateCourseStatus(courseKey, 'currentCustomLanguage', languageCode);
      updateCourseStatus(
        courseKey,
        'customLanguageProgress',
        `${languageIndex + 1}/${totalLanguages} (${languageCode})`
      );

      log(
        `Running translations for ${languageCode} (customisation content with firmCode=${customisationFirmCode})...`
      );
      const customisationTranslated = await runTranslations(
        customisationCourseId,
        languageCode,
        customisationFirmCode
      );
      log(`✅ Customisation translations done for ${languageCode}`);
    } else if (isExistingCustomisation) {
      log(
        `⏭️ Skipping customisation translation for ${languageCode} (no meaningful content beyond languages)`
      );
    }

    // Add delay to allow translations to finish
    await new Promise(resolve => setTimeout(resolve, 8000));

    // Publish translations
    log(`Publishing translations for ${languageCode}...`);
    const publishedTranslations = await publishTranslationsForCourse(
      courseId,
      languageCode
    );
    log(`✅ Published translations for ${languageCode}`);

    return { success: true, languageCode, result: publishedTranslations };
  } catch (error) {
    log(
      `❌ Language processing failed for courseId=${courseId}, language=${languageCode}: ${error.message}`
    );

    return { success: false, languageCode, error: error.message };
  }
}

async function runPipeline(
  courseId: number,
  firmCode: string,
  languageCodes: string[],
  existingCustomisation: any = null
) {
  const courseKey = `${firmCode}_${courseId}`;

  try {
    log(
      `🚀 Processing courseId=${courseId}, firmCode=${firmCode} with ${languageCodes.length} language(s)...`
    );

    // Get the actual customisation courseId (may be different from base courseId)
    const customisationCourseId =
      existingCustomisation?.data?.courseId || courseId;
    const customisationFirmCode =
      existingCustomisation?.data?.firmCode || firmCode;

    let draftResult;

    if (existingCustomisation) {
      // Update existing customisation
      log(`📝 Updating existing ${existingCustomisation.type}...`);
      log(
        `📄 Customisation details: ${customisationFirmCode}_${customisationCourseId}`
      );
      draftResult = await updateExistingCustomisation(
        customisationCourseId,
        customisationFirmCode,
        existingCustomisation,
        languageCodes
      );
    } else {
      // Create new draft
      log(`Creating new draft... ${firmCode} ${courseId}`);
      draftResult = await createDraft(courseId, firmCode, languageCodes);
    }

    // Save the draft (only if we created a new one or if it's a saved customisation)
    if (!existingCustomisation || existingCustomisation.type === 'saved') {
      log(`Saving draft... ${customisationFirmCode} ${customisationCourseId}`);
      const saved = await saveDraft(
        customisationCourseId,
        customisationFirmCode
      );
      log(`✅ Draft saved: ${customisationFirmCode} ${customisationCourseId}`);
    } else {
      log(
        `✅ Using existing draft - no need to save ${customisationFirmCode} ${customisationCourseId}`
      );
    }

    // Set initial status for base translations
    updateCourseStatus(courseKey, 'baseTranslationStatus', 'pending');

    // Process each language for base translations
    const languageResults = [];
    for (let i = 0; i < languageCodes.length; i++) {
      const languageCode = languageCodes[i];
      const result = await processLanguageForCourse(
        courseId,
        firmCode,
        languageCode,
        !!existingCustomisation,
        existingCustomisation?.data?.yaml || null,
        i,
        languageCodes.length,
        customisationCourseId,
        customisationFirmCode
      );
      languageResults.push(result);
    }

    // Update base translation status based on results
    const allLanguagesSuccessful = languageResults.every(r => r.success);
    if (allLanguagesSuccessful) {
      updateCourseStatus(courseKey, 'baseTranslationStatus', 'success');
    } else {
      updateCourseStatus(
        courseKey,
        'baseTranslationStatus',
        'error',
        'Some languages failed'
      );
    }

    // Handle custom translation status
    if (
      existingCustomisation &&
      hasCustomisationContent(existingCustomisation.data?.yaml || '')
    ) {
      updateCourseStatus(courseKey, 'customTranslationStatus', 'pending');
      // Custom translations were processed during language processing
      if (allLanguagesSuccessful) {
        updateCourseStatus(courseKey, 'customTranslationStatus', 'success');
      } else {
        updateCourseStatus(
          courseKey,
          'customTranslationStatus',
          'error',
          'Some languages failed'
        );
      }
    } else {
      updateCourseStatus(courseKey, 'customTranslationStatus', 'skipped');
    }

    // Update base publishing status
    updateCourseStatus(courseKey, 'basePublishingStatus', 'pending');

    // Publish base course
    log(`Publishing base course...`);
    const publishedCourse = await publishBaseCourse(courseId);
    log(`✅ Published base course: ${firmCode} ${courseId}`);

    updateCourseStatus(courseKey, 'basePublishingStatus', 'success');

    // Update custom publishing status
    updateCourseStatus(courseKey, 'customPublishingStatus', 'pending');

    // Publish custom course (use the actual customisation courseId)
    log(
      `Publishing custom course... ${customisationFirmCode} ${customisationCourseId}`
    );
    const publishedCustomCourse = await publishCustomCourse(
      customisationCourseId,
      customisationFirmCode
    );
    log(
      `✅ Published custom course: ${customisationFirmCode} ${customisationCourseId}`
    );

    updateCourseStatus(courseKey, 'customPublishingStatus', 'success');

    // Summary of language processing
    const successfulLanguages = languageResults
      .filter(r => r.success)
      .map(r => r.languageCode);
    const failedLanguages = languageResults.filter(r => !r.success);

    log(`🎉 Workflow completed for courseId=${courseId}, firmCode=${firmCode}`);
    log(`✅ Successful languages: ${successfulLanguages.join(', ')}`);
    if (failedLanguages.length > 0) {
      log(
        `❌ Failed languages: ${failedLanguages
          .map(f => f.languageCode)
          .join(', ')}`
      );
    }

    return { success: true, languageResults, publishedCourse };
  } catch (error) {
    log(
      `❌ Pipeline stopped for courseId=${courseId}, firmCode=${firmCode}: ${error.message}`
    );

    // Update status to error
    updateCourseStatus(courseKey, 'publishingStatus', 'error', error.message);

    return { success: false, error: error.message };
  }
}

// Computed properties
const coursesToProcess = computed(() => {
  const courseIds = parseCourseIds(courseIdsInput.value);
  const allCourses: any[] = [];

  courseIds.forEach(courseId => {
    const courseKey = `${firmCode.value}_${courseId}`;
    const validationStatus = courseValidationStatus.value.get(courseKey);

    if (validationStatus) {
      allCourses.push({
        courseId: courseId,
        firmCode: firmCode.value,
        isBaseCourse: true,
        hasTranslations: true
      });
    }
  });

  // Sort by courseId
  return allCourses.sort((a, b) => a.courseId - b.courseId);
});

const hasAnyInvalidCourses = computed(() => {
  return Array.from(courseValidationStatus.value?.entries() || []).some(
    ([courseKey, status]) => {
      // Only check validation if the course is selected
      const course = coursesToProcess.value.find(
        c => `${c.firmCode}_${c.courseId}` === courseKey
      );
      return course && (!status.baseCourseValid || !status.customisationValid);
    }
  );
});

const invalidCoursesCount = computed(() => {
  return Array.from(courseValidationStatus.value?.entries() || []).filter(
    ([courseKey, status]) => {
      // Only count invalid courses if the course is selected
      const course = coursesToProcess.value.find(
        c => `${c.firmCode}_${c.courseId}` === courseKey
      );
      return course && (!status.baseCourseValid || !status.customisationValid);
    }
  ).length;
});

const existingCustomisationsCount = computed(() => {
  return existingCustomisations.value.size;
});

// Course selection computed properties
const allCoursesSelected = computed(() => {
  if (coursesToProcess.value.length === 0) return false;
  return coursesToProcess.value.every(course => {
    const courseKey = `${course.firmCode}_${course.courseId}`;
    return selectedCourses.value.has(courseKey);
  });
});

const selectedCoursesCount = computed(() => {
  return selectedCourses.value.size;
});

// Individual field validation
const courseIdValidationErrors = computed(() => {
  const errors: string[] = [];

  if (!courseIdsInput.value || courseIdsInput.value.trim().length === 0) {
    return errors; // Don't show errors for empty field
  }

  // Check for unwanted spaces
  if (courseIdsInput.value !== courseIdsInput.value.trim()) {
    errors.push('Course IDs cannot have leading or trailing spaces');
  }

  // Check for spaces in the middle (spaces after comma are optional)
  const parts = courseIdsInput.value.split(',');
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i === 0) {
      // First part: no leading spaces allowed
      if (part.startsWith(' ')) {
        errors.push('Course IDs cannot have spaces before the first ID');
        break;
      }
    } else {
      // Subsequent parts: optional single space after comma, but no multiple spaces
      if (
        part.startsWith('  ') ||
        (part.startsWith(' ') && !part.match(/^ [0-9]+$/))
      ) {
        errors.push(
          'Only a single space is allowed after commas in course IDs'
        );
        break;
      }
    }
  }

  const courseIds = parseCourseIds(courseIdsInput.value);
  if (courseIds.length === 0) {
    errors.push('At least one valid course ID is required');
  } else {
    // Check for invalid course IDs
    const invalidIds = courseIdsInput.value
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0)
      .filter(id => isNaN(parseInt(id)) || parseInt(id) <= 0);

    if (invalidIds.length > 0) {
      errors.push(`Invalid course IDs: ${invalidIds.join(', ')}`);
    }

    // Check for duplicates
    const uniqueIds = [...new Set(courseIds)];
    if (uniqueIds.length !== courseIds.length) {
      errors.push('Duplicate course IDs are not allowed');
    }

    // Check for reasonable range
    const invalidRange = courseIds.filter(id => id < 1 || id > 9999999);
    if (invalidRange.length > 0) {
      errors.push(
        `Course IDs out of range: ${invalidRange.join(
          ', '
        )} (must be between 1 and 9999999)`
      );
    }
  }

  return errors;
});

const firmCodeValidationErrors = computed(() => {
  const errors: string[] = [];

  if (!firmCode.value || firmCode.value.trim().length === 0) {
    return errors; // Don't show errors for empty field
  }

  // Check for leading or trailing spaces
  if (firmCode.value !== firmCode.value.trim()) {
    errors.push('Firm code cannot have leading or trailing spaces');
  }

  if (firmCode.value.trim().length < 3) {
    errors.push('Firm code must be at least 3 characters long');
  }

  if (firmCode.value.trim().length > 50) {
    errors.push('Firm code must be no more than 50 characters long');
  }

  if (!/^[a-zA-Z0-9_-]+$/.test(firmCode.value.trim())) {
    errors.push(
      'Firm code can only contain letters, numbers, hyphens, and underscores'
    );
  }

  // Check for common invalid patterns
  if (/^[0-9]+$/.test(firmCode.value.trim())) {
    errors.push('Firm code cannot be only numbers');
  }

  if (
    firmCode.value.trim().startsWith('-') ||
    firmCode.value.trim().endsWith('-')
  ) {
    errors.push('Firm code cannot start or end with a hyphen');
  }

  return errors;
});

const languageValidationErrors = computed(() => {
  const errors: string[] = [];

  if (!languageCodes.value || languageCodes.value.trim().length === 0) {
    return errors; // Don't show errors for empty field
  }

  // Check for unwanted spaces
  if (languageCodes.value !== languageCodes.value.trim()) {
    errors.push('Language codes cannot have leading or trailing spaces');
  }

  // Check for spaces in the middle (spaces after comma are optional)
  const parts = languageCodes.value.split(',');
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i === 0) {
      // First part: no leading spaces allowed
      if (part.startsWith(' ')) {
        errors.push('Language codes cannot have spaces before the first code');
        break;
      }
    } else {
      // Subsequent parts: optional single space after comma, but no multiple spaces
      // Just check for multiple spaces - actual format validation happens against languages.json
      if (
        part.startsWith('  ') ||
        (part.startsWith(' ') && part.trim().length === 0)
      ) {
        errors.push(
          'Only a single space is allowed after commas in language codes'
        );
        break;
      }
    }
  }

  const languages = parseLanguageCodes(languageCodes.value);
  if (languages.length === 0) {
    errors.push('At least one language code is required');
  } else {
    // Check for invalid language codes (not in languages.json)
    const invalidLanguages = languages.filter(lang => !languagesMap[lang]);

    if (invalidLanguages.length > 0) {
      errors.push(
        `Invalid language codes: ${invalidLanguages.join(
          ', '
        )} (not found in language list)`
      );
    }

    // Check for deprecated languages
    const deprecatedCodes = languages.filter(
      lang => languagesMap[lang]?.deprecated
    );

    if (deprecatedCodes.length > 0) {
      const deprecatedNames = deprecatedCodes
        .map(code => `${languagesMap[code]?.english || code} (${code})`)
        .join(', ');
      errors.push(
        `Deprecated language codes are not allowed: ${deprecatedNames}. Please use supported language codes.`
      );
    }

    // Check for languages not in active languages list
    const inactiveLanguages = languages.filter(
      lang => languagesMap[lang] && !activeLanguages.includes(lang)
    );

    if (inactiveLanguages.length > 0) {
      errors.push(
        `Inactive language codes: ${inactiveLanguages.join(
          ', '
        )} (not in active languages list)`
      );
    }

    // Check for duplicates
    const uniqueLanguages = [...new Set(languages)];
    if (uniqueLanguages.length !== languages.length) {
      errors.push('Duplicate language codes are not allowed');
    }

    // Check for common invalid patterns (language names instead of codes)
    const commonInvalid = languages.filter(lang =>
      ['english', 'french', 'german', 'spanish', 'italian'].includes(
        lang.toLowerCase()
      )
    );
    if (commonInvalid.length > 0) {
      errors.push(
        `Use language codes, not names: ${commonInvalid.join(
          ', '
        )} (try 'en', 'fr', 'de', 'es', 'it')`
      );
    }
  }

  return errors;
});

// Overall validation
const isValidFirmCode = computed(() => {
  return (
    firmCode.value &&
    firmCode.value.trim().length > 0 &&
    firmCodeValidationErrors.value.length === 0
  );
});

const inputValidationErrors = computed(() => {
  const errors: string[] = [];

  // Only show errors if fields have content
  if (courseIdsInput.value && courseIdsInput.value.trim().length > 0) {
    errors.push(...courseIdValidationErrors.value);
  } else {
    errors.push('At least one valid course ID is required');
  }

  if (firmCode.value && firmCode.value.trim().length > 0) {
    errors.push(...firmCodeValidationErrors.value);
  } else {
    errors.push('Firm code is required');
  }

  if (languageCodes.value && languageCodes.value.trim().length > 0) {
    errors.push(...languageValidationErrors.value);
  } else {
    errors.push('At least one language code is required');
  }

  return errors;
});

const isValidInput = computed(() => {
  return inputValidationErrors.value.length === 0;
});

// Methods
const parseCourseIds = (input: string): number[] => {
  return input
    .split(',')
    .map(id => id.trim())
    .filter(id => id.length > 0)
    .map(id => parseInt(id))
    .filter(id => !isNaN(id) && id > 0);
};

const parseLanguageCodes = (input: string): string[] => {
  return input
    .split(',')
    .map(lang => lang.trim())
    .filter(lang => lang.length > 0);
};

const getTranslationLanguages = (course: any): string[] => {
  // This would be populated based on the course's available languages
  return parseLanguageCodes(languageCodes.value);
};

const isCourseSelected = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return selectedCourses.value.has(courseKey);
};

const toggleCourseSelection = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  if (selectedCourses.value.has(courseKey)) {
    selectedCourses.value.delete(courseKey);
  } else {
    selectedCourses.value.add(courseKey);
  }
};

const toggleSelectAll = (e: Event) => {
  const { checked } = e.target as HTMLInputElement;
  if (checked) {
    // Select all valid courses (both base course and customisation must be valid)
    coursesToProcess.value.forEach(course => {
      const courseKey = `${course.firmCode}_${course.courseId}`;
      if (isCourseValidForSelection(course)) {
        selectedCourses.value.add(courseKey);
      }
    });
  } else {
    // Deselect all
    selectedCourses.value.clear();
  }
};

const getValidationStatus = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseValidationStatus.value.get(courseKey);
};

const getBaseCourseValidationStatus = (course: any) => {
  const status = getValidationStatus(course);
  if (!status) return 'pending';
  return status.baseCourseValid ? 'valid' : 'invalid';
};

const getCustomisationValidationStatus = (course: any) => {
  const status = getValidationStatus(course);
  if (!status) return 'pending';
  return status.customisationValid ? 'valid' : 'invalid';
};

const getBaseCourseValidationTooltip = (course: any) => {
  const status = getValidationStatus(course);
  if (!status) return 'Base course validation pending...';

  if (status.baseCourseValid) {
    return 'Base course is valid and ready for translations';
  } else {
    let errorMessage = status.baseCourseError || 'Unknown error';
    if (status.baseCourseErrorCode) {
      const errorDescription = getErrorDescription(status.baseCourseErrorCode);
      errorMessage = `${errorDescription}: ${errorMessage}`;
    }
    return `Base course validation failed: ${errorMessage}`;
  }
};

const getCustomisationValidationTooltip = (course: any) => {
  const status = getValidationStatus(course);
  if (!status) return 'Customisation validation pending...';

  if (status.customisationValid) {
    return 'Customisation is valid and ready for processing';
  } else {
    let errorMessage = status.customisationError || 'Unknown error';
    if (status.customisationErrorCode) {
      const errorDescription = getErrorDescription(
        status.customisationErrorCode
      );
      errorMessage = `${errorDescription}: ${errorMessage}`;
    }
    return `Customisation validation failed: ${errorMessage}`;
  }
};

const isCourseValidForSelection = (course: any) => {
  const status = getValidationStatus(course);
  if (!status) return false;
  return status.baseCourseValid && status.customisationValid;
};

const hasExistingCustomisation = (course: any) => {
  const courseId = course.courseId;
  return existingCustomisations.value.has(courseId);
};

// Status getter methods
const getBaseTranslationStatus = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseStatus.value.get(courseKey)?.baseTranslationStatus || 'idle';
};

const getCustomTranslationStatus = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseStatus.value.get(courseKey)?.customTranslationStatus || 'idle';
};

const getPublishingStatus = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseStatus.value.get(courseKey)?.publishingStatus || 'idle';
};

const getOverallStatus = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseStatus.value.get(courseKey)?.overallStatus || 'idle';
};

const getBaseLanguageProgress = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseStatus.value.get(courseKey)?.baseLanguageProgress;
};

const getCustomLanguageProgress = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseStatus.value.get(courseKey)?.customLanguageProgress;
};

const getBasePublishingStatus = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseStatus.value.get(courseKey)?.basePublishingStatus || 'idle';
};

const getCustomPublishingStatus = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseStatus.value.get(courseKey)?.customPublishingStatus || 'idle';
};

const getCustomisationDetails = (course: any) => {
  const courseId = course.courseId;
  const existingCustomisation = existingCustomisations.value
    .get(courseId)
    ?.list?.find(custom => custom.firmCode === course.firmCode);

  if (existingCustomisation) {
    if (existingCustomisation.courseId !== courseId) {
      return `${existingCustomisation.courseId}`;
    } else {
      return `${courseId}`;
    }
  }

  return null;
};

// Computed properties for failed courses
const failedCourses = computed(() => {
  return coursesToProcess.value.filter(course => {
    const courseKey = `${course.firmCode}_${course.courseId}`;
    const status = courseStatus.value.get(courseKey);
    return (
      status &&
      (status.baseTranslationStatus === 'error' ||
        status.customTranslationStatus === 'error' ||
        status.basePublishingStatus === 'error' ||
        status.customPublishingStatus === 'error')
    );
  });
});

// Helper function to update course status
const updateCourseStatus = (
  courseKey: string,
  statusType: keyof CourseStatus,
  status: string,
  error?: string
) => {
  const currentStatus = courseStatus.value.get(courseKey) || {
    baseTranslationStatus: 'idle',
    customTranslationStatus: 'idle',
    basePublishingStatus: 'idle',
    customPublishingStatus: 'idle'
  };

  (currentStatus as any)[statusType] = status;
  if (error && statusType.endsWith('Error')) {
    (currentStatus as any)[statusType] = error;
  }

  courseStatus.value.set(courseKey, currentStatus);
};

// Validation functions using countCustomisations
const validateCourse = async (courseId: number, firmCode: string) => {
  const courseKey = `${firmCode}_${courseId}`;

  try {
    log(`🔍 Validating courseId=${courseId}, firmCode=${firmCode}...`);

    let baseCourseValid = false;
    let customisationValid = false;
    let baseCourseError = '';
    let customisationError = '';
    let baseCourseErrorCode: number | undefined;
    let customisationErrorCode: number | undefined;
    let existingCustomisation = null;

    // Validate base course (translations readiness)
    try {
      const baseCourse = await getLatestSaved({ courseId });
      if (!baseCourse) {
        throw new Error(`No base course found for courseId=${courseId} [404]`);
      } else if (baseCourse.activityName === 'Conversational Learning') {
        throw new Error(
          `Base course is a Conversational Learning course and cannot be used for bulk customisations [404]`
        );
      }

      log(`Checking translations readiness for courseId=${courseId}...`);
      await checkTranslationsDryRun(courseId);
      log(`✅ Base course translations ready for courseId=${courseId}`);
      baseCourseValid = true;
    } catch (error) {
      log(
        `❌ Base course validation failed for courseId=${courseId}: ${error.message}`
      );
      baseCourseError = error.message;
      baseCourseErrorCode = extractErrorCode(error);
    }

    // Only check customisations if base course validation passed
    if (baseCourseValid) {
      try {
        log(`🔍 Searching for customisations for courseId=${courseId}...`);
        const customReport = await countCustomisations(courseId);
        if (customReport?.total > 0) {
          log(
            `📊 Found ${customReport?.total} customisation(s) for courseId=${courseId}`
          );

          // Look for customisation with matching firmCode
          const matchingCustomisation = customReport?.list?.find(
            custom =>
              custom.firmCode === firmCode && custom.courseId === courseId
          );

          if (matchingCustomisation) {
            log(
              `📄 Found existing customisation: ${matchingCustomisation.firmCode}_${matchingCustomisation.courseId}`
            );

            // Check for unsaved changes by comparing with draft
            try {
              const latestDraft = await getLatestDraft({
                courseId: matchingCustomisation.courseId,
                firmCode: matchingCustomisation.firmCode
              });

              if (latestDraft) {
                const hasUnsavedChanges =
                  latestDraft.metadata.updatedDate >
                  matchingCustomisation.updatedDate;
                if (hasUnsavedChanges) {
                  throw new Error(
                    `Draft has unsaved changes (${matchingCustomisation.courseId}, ${matchingCustomisation.firmCode})`
                  );
                }
              }

              existingCustomisation = {
                type: 'saved',
                data: matchingCustomisation
              };

              // Store the customisation for later use
              existingCustomisations.value.set(courseId, existingCustomisation);
            } catch (draftError) {
              throw new Error(
                `Customisation validation failed: ${draftError.message}`
              );
            }
          } else {
            log(
              `✅ No existing customisation with firmCode=${firmCode} for courseId=${courseId} - will create new`
            );
          }
        } else {
          log(
            `✅ No existing customisations for courseId=${courseId} - will create new`
          );
        }

        customisationValid = true;
      } catch (error) {
        log(
          `❌ Customisation validation failed for courseId=${courseId}: ${error.message}`
        );
        customisationError = error.message;
        customisationErrorCode = extractErrorCode(error);
      }
    } else {
      log(
        `⏭️ Skipping customisation validation for courseId=${courseId} due to base course validation failure`
      );
      customisationValid = false;
      customisationError = 'Skipped due to base course validation failure';
    }

    // Set validation status
    courseValidationStatus.value.set(courseKey, {
      baseCourseValid,
      customisationValid,
      hasExistingDraftOrSaved: !!existingCustomisation,
      translationsReady: baseCourseValid,
      baseCourseError: baseCourseError || undefined,
      customisationError: customisationError || undefined,
      baseCourseErrorCode,
      customisationErrorCode
    });

    const overallValid = baseCourseValid && customisationValid;
    log(
      `✅ Validation completed for ${courseKey} - Base: ${
        baseCourseValid ? '✓' : '❌'
      }, Custom: ${customisationValid ? '✓' : '❌'}`
    );

    return {
      isValid: overallValid,
      baseCourseValid,
      customisationValid,
      existingCustomisation,
      baseCourseError: baseCourseError || undefined,
      customisationError: customisationError || undefined,
      baseCourseErrorCode,
      customisationErrorCode
    };
  } catch (error) {
    log(`❌ Unexpected validation error for ${courseKey}: ${error.message}`);

    const errorCode = extractErrorCode(error);
    courseValidationStatus.value.set(courseKey, {
      baseCourseValid: false,
      customisationValid: false,
      hasExistingDraftOrSaved: false,
      translationsReady: false,
      baseCourseError: error.message,
      customisationError: error.message,
      baseCourseErrorCode: errorCode,
      customisationErrorCode: errorCode
    });
    return {
      isValid: false,
      baseCourseValid: false,
      customisationValid: false,
      error: error.message,
      baseCourseErrorCode: errorCode,
      customisationErrorCode: errorCode
    };
  }
};

const validateAllCourses = async (courseIds: number[], firmCode: string) => {
  validatingCourses.value = true;
  courseValidationStatus.value.clear();
  existingCustomisations.value.clear();
  selectedCourses.value.clear();

  const validCourseIds: number[] = [];
  const failedCourseIds: { courseId: number; error: string }[] = [];

  log(`🔍 Pre-validating all course IDs...`);

  try {
    for (const courseId of courseIds) {
      const result = await validateCourse(courseId, firmCode);
      if (result.isValid) {
        validCourseIds.push(courseId);
        if (result.existingCustomisation) {
          existingCustomisations.value.set(
            courseId,
            result.existingCustomisation
          );
        }
      } else {
        failedCourseIds.push({
          courseId,
          error: result.error || 'Unknown validation error'
        });
      }
    }

    log(`📊 Validation Summary:`);
    log(
      `✅ Valid course IDs: ${validCourseIds.length} - [${validCourseIds.join(
        ', '
      )}]`
    );
    log(`❌ Failed course IDs: ${failedCourseIds.length}`);

    if (failedCourseIds.length > 0) {
      log(`🚫 Skipping failed course IDs:`);
      failedCourseIds.forEach(({ courseId, error }) => {
        log(`  - courseId=${courseId}: ${error}`);
      });
    }

    // Only auto-select if there are no validation errors at all
    if (failedCourseIds.length === 0 && validCourseIds.length > 0) {
      validCourseIds.forEach(courseId => {
        const courseKey = `${firmCode}_${courseId}`;
        selectedCourses.value.add(courseKey);
      });
      log(
        `🎯 Auto-selected all ${validCourseIds.length} valid course(s) for processing`
      );
    } else if (failedCourseIds.length > 0) {
      log(
        `⚠️ Validation errors detected - no courses auto-selected. Please review and manually select valid courses.`
      );
    } else {
      log(`⚠️ No valid courses found.`);
    }

    return { validCourseIds, failedCourseIds };
  } finally {
    validatingCourses.value = false;
  }
};

const checkCustomisations = async () => {
  const courseIds = parseCourseIds(courseIdsInput.value);
  if (courseIds.length === 0) return;

  // Reset all statuses and clear data when starting a new search
  resetTableAndStatuses();

  checkingCustomisations.value = true;

  try {
    log(
      `🚀 Starting bulk customisation process at ${new Date().toISOString()}`
    );

    // First, validate all courses (this is the critical step for customisations)
    const { validCourseIds, failedCourseIds } = await validateAllCourses(
      courseIds,
      firmCode.value
    );

    // Only proceed if we have at least some valid courses
    if (validCourseIds.length === 0) {
      log(`🛑 No valid course IDs to process. Exiting.`);
      return;
    }

    if (failedCourseIds.length > 0) {
      log(
        `⚠️ Some course IDs failed validation but proceeding with ${validCourseIds.length} valid course(s).`
      );
      log(
        `Please review the validation errors and manually select which courses to process.`
      );
    } else {
      log(
        `🚀 All courses validated successfully. Ready to process ${validCourseIds.length} course(s)...`
      );
    }
  } catch (error) {
    log(`Error checking customisations: ${error.message}`);
  } finally {
    checkingCustomisations.value = false;
  }
};

const resetTableAndStatuses = () => {
  // Clear all validation and status data
  courseValidationStatus.value.clear();
  existingCustomisations.value.clear();
  courseStatus.value.clear();
  selectedCourses.value.clear();
  processingResults.value = [];

  // Reset processing state
  resetProcessingState();
};

const clearAll = () => {
  courseIdsInput.value = '';
  firmCode.value = '';
  languageCodes.value = '';
  resetTableAndStatuses();

  updateHashUrl('/bulk-customisations', new URLSearchParams());
};

const retryFailedCourses = async () => {
  if (failedCourses.value.length === 0) return;

  log(`🔄 Retrying ${failedCourses.value.length} failed course(s)...`);

  // Reset status for failed courses
  failedCourses.value.forEach(course => {
    const courseKey = `${course.firmCode}_${course.courseId}`;
    courseStatus.value.set(courseKey, {
      baseTranslationStatus: 'idle',
      customTranslationStatus: 'idle',
      publishingStatus: 'idle',
      overallStatus: 'idle'
    });
  });

  // Start processing the failed courses
  await startBulkCustomisations();
};

const resetProcessingState = () => {
  isProcessing.value = false;
  shouldStopProcessing.value = false;
  isCreatingDrafts.value = false;
  isCreatingTranslations.value = false;
  isPublishing.value = false;
  currentDraftProcessing.value = '';
  currentTranslationProcessing.value = '';
  currentPublishingProcessing.value = '';
  completedDrafts.value = 0;
  totalDrafts.value = 0;
  completedTranslations.value = 0;
  totalTranslations.value = 0;
  completedPublishing.value = 0;
  totalPublishing.value = 0;
};

const startBulkCustomisations = async () => {
  if (selectedCourses.value.size === 0) return;

  isProcessing.value = true;
  shouldStopProcessing.value = false;
  processingResults.value = [];

  const languageCodesList = parseLanguageCodes(languageCodes.value);
  const courseIds = parseCourseIds(courseIdsInput.value);
  const validCourseIds = courseIds.filter(courseId => {
    const courseKey = `${firmCode.value}_${courseId}`;
    return selectedCourses.value.has(courseKey);
  });

  try {
    log(
      `🚀 Starting bulk customisations for ${validCourseIds.length} course(s)...`
    );
    log(`Firm code: ${firmCode.value}`);
    log(`Languages: ${languageCodesList.join(', ')}`);

    // Process each course sequentially (following Node script approach)
    for (const courseId of validCourseIds) {
      if (shouldStopProcessing.value) break;

      const courseKey = `${firmCode.value}_${courseId}`;
      const courseStartTime = Date.now();
      log(
        `📚 Starting courseId=${courseId} at ${new Date(
          courseStartTime
        ).toISOString()}`
      );

      try {
        // Get existing customisation from validation phase
        const existingCustomisation =
          existingCustomisations.value.get(courseId) || null;

        const result = await runPipeline(
          courseId,
          firmCode.value,
          languageCodesList,
          existingCustomisation
        );

        const courseEndTime = Date.now();
        const courseDuration = courseEndTime - courseStartTime;
        log(
          `⏱️ CourseId=${courseId} completed in ${(
            courseDuration / 1000
          ).toFixed(2)} seconds`
        );

        processingResults.value.push({
          courseKey,
          success: result.success,
          details: result.success ? 'Successfully processed' : result.error
        });
      } catch (error) {
        log(`❌ Error processing courseId=${courseId}: ${error.message}`);
        processingResults.value.push({
          courseKey,
          success: false,
          error: error.message
        });
      }
    }

    // Final summary
    const successful = processingResults.value.filter(r => r.success).length;
    const failed = processingResults.value.filter(r => !r.success).length;

    log(`📋 Final Summary:`);
    log(`✅ Successfully processed: ${successful} course(s)`);
    if (failed > 0) {
      log(`❌ Failed: ${failed} course(s)`);
      processingResults.value
        .filter(r => !r.success)
        .forEach(result => {
          log(`  - ${result.courseKey}: ${result.error}`);
        });
    }
    log(`🌐 Languages processed: [${languageCodesList.join(', ')}]`);
  } catch (error) {
    log(`Error in bulk customisations: ${error.message}`);
  } finally {
    isProcessing.value = false;
  }
};

const stopProcessing = () => {
  shouldStopProcessing.value = true;
};

const queueForBackgroundProcessing = async () => {
  const courseIds = parseCourseIds(courseIdsInput.value).filter(courseId => {
    const courseKey = `${firmCode.value}_${courseId}`;
    return selectedCourses.value.has(courseKey);
  });

  if (courseIds.length === 0) return;

  const courses: { courseId: number; firmCode: string }[] = [];
  for (const courseId of courseIds) {
    courses.push({ courseId, firmCode: '' });
    if (firmCode.value) courses.push({ courseId, firmCode: firmCode.value });
  }

  isQueuingJob.value = true;
  queueJobMessage.value = '';

  try {
    await submitBulkTranslationJob({
      courses,
      languages: parseLanguageCodes(languageCodes.value),
      provider: translationProvider.value,
      refreshAi: bulkJobRefreshAi.value,
      publishToProduction: bulkJobPublishToProduction.value
    });
    queueJobMessage.value =
      '✓ Job queued — view <a href="#/bulk-translation-jobs" target="_blank" class="text-blue-500">translation jobs</a> to track progress';
  } catch (e: any) {
    queueJobMessage.value = `❌ Failed to queue job: ${e.message}`;
  } finally {
    isQueuingJob.value = false;
  }
};

// URL parameter parsing using hash-based parameters
const parseUrlParameters = () => {
  resetTableAndStatuses();

  log('Parsing URL parameters');
  const urlParams = parseHashParams();

  // Parse course IDs from URL
  const courseIdsParam =
    urlParams.get('courseIds') ||
    urlParams.get('courseids') ||
    urlParams.get('courses');
  if (courseIdsParam) {
    courseIdsInput.value = courseIdsParam;
  }

  // Parse firm code from URL
  const firmCodeParam =
    urlParams.get('firmCode') ||
    urlParams.get('firmcode') ||
    urlParams.get('firm');
  if (firmCodeParam) {
    firmCode.value = firmCodeParam;
  }

  // Parse languages from URL
  const languagesParam =
    urlParams.get('languages') ||
    urlParams.get('langs') ||
    urlParams.get('lang');
  if (languagesParam) {
    languageCodes.value = languagesParam;
  }

  // Parse translation provider from URL
  const translationProviderParam =
    urlParams.get('translationProvider') ||
    urlParams.get('translationprovider') ||
    urlParams.get('provider');
  if (
    translationProviderParam &&
    ['azure', 'openai'].includes(translationProviderParam)
  ) {
    translationProvider.value = translationProviderParam;
  }

  // autoValidate param
  const autoValidateParam = urlParams.get('autoValidate') === 'true';

  // Auto-validate if all parameters are provided
  const hasAllParams = courseIdsParam && firmCodeParam && languagesParam;
  if (hasAllParams && autoValidateParam) {
    // Small delay to ensure reactive updates are processed
    setTimeout(() => {
      checkCustomisations();
    }, 100);
  }
};

// Debounced URL parameter parsing to avoid multiple calls
let parseTimeout: NodeJS.Timeout | null = null;
const debouncedParseUrlParameters = () => {
  if (parseTimeout) {
    clearTimeout(parseTimeout);
  }
  parseTimeout = setTimeout(() => {
    if (route.path === '/bulk-customisations') {
      parseUrlParameters();
    }
  }, 50);
};

// Watch for route changes and re-parse parameters
watch(
  () => route.path,
  () => {
    if (route.path === '/bulk-customisations') {
      debouncedParseUrlParameters();
    }
  },
  { immediate: true }
);

// Watch for hash changes (including query parameters)
watch(
  () => route.fullPath,
  () => {
    if (route.path === '/bulk-customisations') {
      debouncedParseUrlParameters();
    }
  },
  { immediate: true }
);

// Listen for browser navigation events
let handleHashChange: (() => void) | null = null;

onMounted(() => {
  handleHashChange = () => {
    if (route.path === '/bulk-customisations') {
      debouncedParseUrlParameters();
    }
  };

  window.addEventListener('popstate', handleHashChange);
  window.addEventListener('hashchange', handleHashChange);
});

onUnmounted(() => {
  if (parseTimeout) {
    clearTimeout(parseTimeout);
  }
  if (handleHashChange) {
    window.removeEventListener('popstate', handleHashChange);
    window.removeEventListener('hashchange', handleHashChange);
  }
});
</script>
